// src/components/Modules/Vehicles/VehicleInfo.jsx

import React from "react";
import {
  Form,
  InputNumber,
  Input,
  Select,
  Row,
  Col,
  Typography,
  DatePicker,
  Alert,
} from "antd";
import { WarningOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "../../../utils/dayjs"; // Usar dayjs configurado con timezone

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

// Función auxiliar para generar opciones de Select
const generateSelectOptions = (options, zonas) => {
  if (Array.isArray(options)) {
    return options.map((option) => (
      <Option key={option} value={option}>
        {option}
      </Option>
    ));
  } else if (options === "zonas") {
    return zonas.map((zona) => (
      <Option key={zona.id} value={zona.id}>
        {zona.nombre}
      </Option>
    ));
  }
  return null;
};

// Definir una estructura de datos para los campos del vehículo
const vehicleFields = [
  { label: "Móvil", name: "codigo", type: "text", required: true },
  { label: "Placa", name: "placa", type: "text", required: true },
  { label: "Marca", name: "marca", type: "text" },
  { label: "Linea", name: "linea", type: "text" },
  {
    label: "Tipo de Vehículo",
    name: "tipo",
    type: "select",
    options: [
      "Automóvil",
      "Camioneta",
      "Campero",
      "Rampa",
      "Microbús",
      "Bus",
      "Buseta",
      "Van",
      "Furgón",
    ],
    required: true,
  },
  { label: "Modelo", name: "modelo", type: "text" },
  { label: "Color", name: "color", type: "text" },
  {
    label: "Estado",
    name: "estado",
    type: "select",
    options: ["Activo", "Inactivo", "Suspendido", "Retirado"], // Opciones para el Select
    required: true,
  },
  {
    label: "Capacidad de Pasajeros",
    name: "capacidad_pasajeros",
    type: "number",
  },
  { label: "Kilometraje", name: "kilometraje", type: "number" },
  { label: "Número de Motor", name: "numero_motor", type: "text" },
  { label: "Número de Chasis", name: "numero_chasis", type: "text" },
  {
    label: "Zona",
    name: "zona_id",
    type: "select",
    options: "zonas",
    required: true,
  },
  {
    label: "Tipo de Vinculación",
    name: "tipo_vinculacion",
    type: "select",
    options: [
      "ALQUILADO/TERCERO",
      "PROPIO",
      "RENTING",
      "PROPIO/LEASING",
      "CONTRATISTA",
      "RENTING/TERCERO",
      "TERCERO",
      "TERCERO/LEASING",
    ],
    required: true,
  },
  {
    label: "Fecha de Vinculación",
    name: "fecha_vinculacion",
    type: "date",
    required: true,
  },
  {
    label: "Fecha de Matrícula",
    name: "fecha_matricula",
    type: "date",
    required: true,
  },
  { label: "Combustible", name: "combustible", type: "text" },
  { label: "Cilindraje", name: "cilindraje", type: "number" },
  { label: "GPS", name: "gps", type: "text" },
  { label: "Número Tarjeta Operación", name: "numero_tarjeta_operacion", type: "text", required: true },
];

const VehicleInfo = ({
  vehicle,
  handleInputChange,
  zonas,
  errors,
  isEdit = false,
  initialEstado = null,
  estadoObservacion = "",
  onEstadoObservacionChange = () => {},
}) => {
  // Verificar si el estado cambió (solo en modo edición)
  const estadoCambio = isEdit && initialEstado && vehicle.estado !== initialEstado;

  // Función auxiliar para renderizar el componente de entrada adecuado
  const renderFormInput = (field) => {
    const { type, name, label, options } = field;

    switch (type) {
      case "text":
        return (
          <Input
            name={name}
            value={vehicle[name]}
            onChange={handleInputChange}
          />
        );
      case "number":
        return (
          <InputNumber
            name={name}
            value={vehicle[name] || null}
            onChange={(value) =>
              handleInputChange({
                target: { name, value },
              })
            }
            min={0} // Evitar valores negativos, si aplica
            style={{ width: "100%" }}
          />
        );

      case "select":
        return (
          <Select
            showSearch // Habilita la búsqueda
            optionFilterProp="children" // Permite filtrar por el texto visible
            name={name}
            value={vehicle[name] ? vehicle[name] : undefined}
            onChange={(value) =>
              handleInputChange({
                target: { name, value },
              })
            }
            placeholder={`Seleccione ${label}`}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {generateSelectOptions(options, zonas)}
          </Select>
        );

      case "date":
        return (
          <DatePicker
            name={name}
            value={vehicle[name] ? dayjs(vehicle[name]) : null}
            onChange={(date, dateString) =>
              handleInputChange({
                target: { name, value: dateString },
              })
            }
            format="YYYY-MM-DD"
            style={{ width: "100%" }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Title
        level={4}
        style={{
          backgroundColor: "#0a2e5c",
          color: "white",
          padding: "8px 16px",
          borderRadius: "4px",
          marginTop: 24,
          marginBottom: 16,
        }}
      >
        Información del Vehículo
      </Title>
      <Row gutter={16}>
        {vehicleFields.map(({ label, name, type, required, options }) => (
          <Col xs={24} sm={12} key={name}>
            <Form.Item
              label={label}
              required={required}
              validateStatus={errors[name] ? "error" : ""}
              help={errors[name]}
            >
              {renderFormInput({ label, name, type, options })}
            </Form.Item>
          </Col>
        ))}

        {/* Campo de observación cuando cambia el estado */}
        {estadoCambio && (
          <Col xs={24}>
            <Alert
              message={`Cambio de estado: ${initialEstado} → ${vehicle.estado}`}
              description="Se requiere una observación para justificar el cambio de estado."
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
            />
            <Form.Item
              label="Observación del cambio de estado"
              required
              validateStatus={errors.observacion_cambio_estado ? "error" : ""}
              help={errors.observacion_cambio_estado}
            >
              <TextArea
                rows={3}
                placeholder="Ingrese el motivo del cambio de estado (obligatorio)"
                value={estadoObservacion}
                onChange={(e) => onEstadoObservacionChange(e.target.value)}
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </>
  );
};

VehicleInfo.propTypes = {
  vehicle: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  zonas: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  initialEstado: PropTypes.string,
  estadoObservacion: PropTypes.string,
  onEstadoObservacionChange: PropTypes.func,
};

export default VehicleInfo;
